import { useState } from "react";
import { motion } from "framer-motion";
import { Users, Megaphone, Leaf, ArrowUpRight, Crown, Medal, Award, BadgeCheck, Send, Loader2, CheckCircle2 } from "lucide-react";
import SectionHead from "./SectionHead";
import API, { formatApiErrorDetail } from "../../lib/api";
import { fadeUp, stagger, sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

const VALUE_ICONS = [Users, Megaphone, Leaf];
const TIER_DATA = [
  { name: "Gold", icon: Medal, border: "border-amber-400/40", text: "text-amber-400" },
  { name: "Silver", icon: Award, border: "border-slate-300/40", text: "text-slate-300" },
  { name: "Bronze", icon: BadgeCheck, border: "border-orange-600/40", text: "text-orange-500" },
];

const EMPTY = { azienda: "", referente: "", email: "", telefono: "", messaggio: "" };

export default function Sponsor() {
  const { t } = useLang();
  const c = t.sponsor;
  const f = c.form;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.azienda.trim() || !form.referente.trim() || !form.email.trim()) {
      setError(f.error);
      return;
    }
    setStatus("loading");
    try {
      await API.post("/partenariato", form);
      setStatus("success");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  return (
    <section id="sponsor" className={`relative bg-vg-black ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="05" kicker={c.kicker} title={c.title} en={c.en} />

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid gap-5 lg:grid-cols-3">
          {c.values.map((v, i) => (
            <motion.article key={v.title} variants={fadeUp} className="border border-white/10 bg-vg-card p-8" data-testid={`sponsor-value-${i}`}>
              {VALUE_ICONS[i] && (() => { const Icon = VALUE_ICONS[i]; return <Icon className="h-8 w-8 text-vg-green" />; })()}
              <h3 className="mt-5 font-display text-2xl font-semibold uppercase tracking-wider text-vg-white">{v.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-vg-muted">{v.desc}</p>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-12 overflow-hidden border border-vg-green/40 bg-gradient-to-r from-vg-green/15 via-vg-card to-vg-card p-8 sm:p-12"
          data-testid="tier-platinum"
        >
          <Crown className="absolute right-6 top-6 h-10 w-10 text-vg-green/30" aria-hidden="true" />
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-vg-green">{c.platinumEyebrow}</p>
          <h3 className="mt-3 font-display text-4xl font-bold uppercase tracking-wide text-vg-white sm:text-5xl">{c.platinumTitle}</h3>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-vg-muted sm:text-base">{c.platinumDesc}</p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {c.platinumSlots.map((slot, i) => (
              <div
                key={slot}
                className={`flex h-20 items-center justify-center border border-dashed px-2 text-center font-mono text-[10px] uppercase tracking-[0.2em] ${
                  i === 0 ? "border-vg-green/60 bg-vg-green/10 text-vg-green" : "border-white/20 text-vg-muted"
                }`}
              >
                {slot}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="mt-6 grid gap-5 sm:grid-cols-3">
          {TIER_DATA.map((tier, i) => (
            <motion.article key={tier.name} variants={fadeUp} className={`border bg-vg-card p-8 ${tier.border}`} data-testid={`tier-${tier.name.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <tier.icon className={`h-7 w-7 ${tier.text}`} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-vg-muted">{c.tierLabel(i)}</span>
              </div>
              <h3 className={`mt-5 font-display text-3xl font-bold uppercase tracking-wide ${tier.text}`}>{tier.name}{c.tierSponsor}</h3>
              <div className="mt-6 grid h-20 items-center justify-center border border-dashed border-white/20">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{c.yourLogo}</span>
              </div>
            </motion.article>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="mt-12 overflow-hidden border border-white/10 bg-vg-asphalt"
          data-testid="sponsor-form-section"
        >
          <div className="grid lg:grid-cols-12">
            <div className="flex flex-col justify-center gap-6 border-b border-white/10 p-8 sm:p-10 lg:col-span-5 lg:border-b-0 lg:border-r">
              <div>
                <h3 className="font-display text-3xl font-bold uppercase tracking-wide text-vg-white sm:text-4xl">{f.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-vg-muted sm:text-base">{c.ctaText}</p>
                <p className="mt-4 text-sm leading-relaxed text-vg-muted">{f.intro}</p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">Platinum Host · Gold · Silver · Bronze</p>
                <a
                  href="mailto:info@evgpitalia.it?subject=Sponsorship%20EVGP%20Italia%202027"
                  data-testid="sponsor-mailto"
                  className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-vg-muted transition-colors hover:text-vg-green"
                >
                  info@evgpitalia.it
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>

            <div className="p-8 sm:p-10 lg:col-span-7">
              {status === "success" ? (
                <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="sponsor-success">
                  <CheckCircle2 className="h-14 w-14 text-vg-green" />
                  <h4 className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-vg-white">{f.successTitle}</h4>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-vg-muted">{f.success}</p>
                  <button
                    onClick={() => { setForm(EMPTY); setStatus("idle"); }}
                    data-testid="sponsor-reset-button"
                    className="mt-7 rounded-full border border-vg-white/25 px-6 py-3 font-display text-sm font-semibold uppercase tracking-wider text-vg-white transition-colors hover:border-vg-green hover:text-vg-green"
                  >
                    {f.reset}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="sponsor-form">
                  <div>
                    <label htmlFor="sponsor-azienda" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{f.azienda}</label>
                    <input id="sponsor-azienda" type="text" required value={form.azienda} onChange={set("azienda")} data-testid="sponsor-azienda" className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green" />
                  </div>
                  <div>
                    <label htmlFor="sponsor-referente" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{f.referente}</label>
                    <input id="sponsor-referente" type="text" required value={form.referente} onChange={set("referente")} data-testid="sponsor-referente" className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green" />
                  </div>
                  <div>
                    <label htmlFor="sponsor-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{f.email}</label>
                    <input id="sponsor-email" type="email" required value={form.email} onChange={set("email")} data-testid="sponsor-email" className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green" />
                  </div>
                  <div>
                    <label htmlFor="sponsor-telefono" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{f.telefono}</label>
                    <input id="sponsor-telefono" type="tel" value={form.telefono} onChange={set("telefono")} data-testid="sponsor-telefono" className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="sponsor-messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{f.messaggio}</label>
                    <textarea id="sponsor-messaggio" rows="3" value={form.messaggio} onChange={set("messaggio")} data-testid="sponsor-messaggio" className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green" />
                  </div>
                  {error && (
                    <p className="rounded border border-vg-red/40 bg-vg-red/10 px-4 py-3 text-sm text-vg-red sm:col-span-2" data-testid="sponsor-error">
                      {error}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="sponsor-submit"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-vg-green px-8 py-4 font-display text-lg font-semibold uppercase tracking-wider text-vg-black transition-all duration-200 hover:shadow-[0_0_36px_rgba(16,185,129,0.55)] disabled:opacity-60 sm:w-auto"
                    >
                      {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                      {status === "loading" ? f.sending : f.submit}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
